import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  BookOpen,
  Check,
  ChevronLeft,
  ChevronRight,
  Compass,
  Cpu,
  FileText,
  Infinity as InfinityIcon,
  Layers,
  LayoutGrid,
  LineChart,
  Network,
  Quote,
  ShieldCheck,
  Sparkles,
  Star,
  User,
  Users,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import bullioHomeHero from "@/assets/bullio-home-hero.webp";
import bullioHomeHero640 from "@/assets/bullio-home-hero-640.webp";
import bullioHomeHero800 from "@/assets/bullio-home-hero-800.webp";
import bullioWave from "@/assets/bullio-lp-hero.webp";
import bullioWave360 from "@/assets/bullio-lp-hero-360.webp";
import advisorPhoto from "@/assets/home-advisor.webp";
import advisorPhoto600 from "@/assets/home-advisor-600.webp";
import { GoogleGlyph } from "@/components/GoogleReviewCard";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import VideoTestimonials from "@/components/VideoTestimonials";
import EnrollButton from "@/components/EnrollButton";
import { faqSchema, organizationSchema, websiteSchema } from "@/lib/structured-data";

// Still feeds the FAQ structured data for search engines.
const homeFaqs = [
  {
    question: "What does Bullio Academy teach?",
    answer:
      "Bullio Academy teaches professional trading skills across five parts and seventeen chapters: market foundations, price action, technical indicators, risk management, trading psychology, and live market analysis. Lessons are designed for traders progressing from beginner to consistent execution.",
  },
  {
    question: "Who is Bullio Academy for?",
    answer:
      "It is built for self-directed traders who want a structured curriculum and community. Members include day traders, swing traders, and crypto traders looking to develop a repeatable system rather than rely on signals or guru calls.",
  },
  {
    question: "How is the program delivered?",
    answer:
      "Lessons are delivered online on demand, alongside live market analysis sessions and a private community of 2,400+ traders for accountability and idea sharing.",
  },
  {
    question: "Do I need prior trading experience?",
    answer:
      "No. The curriculum starts from market foundations and progresses through advanced strategy. Existing traders can skip ahead to the modules they need.",
  },
];

const testimonials = [
  { name: "Elena Vasquez", role: "Swing Trader, 8mo", text: "Bullio gave me the structure I was missing. The risk management module alone saved my account." },
  { name: "Marcus Hahn", role: "Day Trader, 1yr", text: "The community is incredible. Real traders, real P&L. No guru nonsense. Best investment I've made." },
  { name: "Priya Lakshmanan", role: "Crypto Trader, 6mo", text: "I went from guessing entries to having a system. The psychology module changed how I think about losses." },
];
const highlights: { icon: LucideIcon; title: string; desc: string }[] = [
  { icon: BookOpen, title: "300+ hours", desc: "Courses, lectures & masterclasses" },
  { icon: Users, title: "Live trading rooms", desc: "Watch and learn in real time" },
  { icon: LineChart, title: "Demo trading account", desc: "Practice with virtual money" },
  { icon: User, title: "Personal education advisor", desc: "Guidance and learning support" },
  { icon: InfinityIcon, title: "Lifetime access", desc: "Current and future content included" },
];

const whyCards: { icon: LucideIcon; title: string; desc: string }[] = [
  { icon: Layers, title: "A structured learning path", desc: "Go from the basics to advanced strategies with a clear roadmap." },
  { icon: Users, title: "Built by traders, for traders", desc: "Learn from real market experience, not just theory." },
  { icon: Compass, title: "Personal guidance", desc: "Get support on what to learn next, setup, and your trading questions." },
  { icon: LineChart, title: "Real market context", desc: "Live trading rooms, practice accounts, and ongoing content keep you connected to the markets." },
];

// Course counts as supplied in the client's design.
const flagshipTopics: { icon: LucideIcon; title: string; desc: string; courses: number }[] = [
  { icon: Cpu, title: "AI Trading", desc: "Learn how to use AI tools and automation in your trading process.", courses: 12 },
  { icon: LineChart, title: "Day Trading", desc: "Strategies, setups and risk management for short-term trading.", courses: 18 },
  { icon: Network, title: "Options Trading", desc: "From the basics to advanced options strategies.", courses: 16 },
  { icon: FileText, title: "Futures Trading", desc: "Learn to trade futures with confidence and discipline.", courses: 14 },
  { icon: ShieldCheck, title: "Risk Management", desc: "Protect your capital and trade with a long-term mindset.", courses: 10 },
];

const advisorPoints = [
  "Help with choosing what to watch next",
  "Help with setup, indicators, and choosing a broker",
  "Ask questions as you learn",
];

const advisorSteps = [
  { title: "Tell us your level", desc: "Share your goals and experience." },
  { title: "Get your learning path", desc: "We'll recommend the right courses for you." },
  { title: "Practice and ask questions", desc: "Put your knowledge into action with support from your advisor." },
];


const Eyebrow = ({ children, light = false }: { children: React.ReactNode; light?: boolean }) => (
  <span
    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[10px] font-semibold uppercase tracking-wider ${
      light ? "border-white/20 bg-white/10 text-white/80" : "border-secondary/25 bg-secondary/5 text-[#2f6e51]"
    }`}
  >
    <Sparkles size={11} />
    {children}
  </span>
);

const BookCallButton = ({ className = "" }: { className?: string }) => (
  <EnrollButton
    size="lg"
    wrapperClassName="w-full sm:w-auto"
    className={`group h-12 w-full bg-secondary px-6 text-base font-semibold shadow-lg shadow-secondary/25 hover:bg-secondary/90 sm:w-auto ${className}`}
  >
    Book your free call
    <ArrowRight size={18} className="ml-2 transition-transform group-hover:translate-x-1" />
  </EnrollButton>
);

/** A small candlestick chart, drawn so it stays sharp at any size. */
const MiniChart = ({ dark = false }: { dark?: boolean }) => {
  const candles = [
    [40, 52, 36, 58], [52, 47, 44, 55], [47, 55, 45, 60], [55, 62, 52, 66], [62, 58, 55, 64],
    [58, 66, 56, 70], [66, 63, 60, 69], [63, 72, 61, 76], [72, 69, 66, 75], [69, 78, 67, 82],
    [78, 74, 71, 80], [74, 83, 72, 87], [83, 80, 77, 86], [80, 88, 78, 92],
  ];
  return (
    <svg viewBox="0 0 300 120" className="h-full w-full" aria-hidden>
      {[30, 60, 90].map((y) => (
        <line key={y} x1="0" x2="300" y1={y} y2={y} stroke={dark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.06)"} />
      ))}
      {candles.map(([o, c, l, h], i) => {
        const x = 12 + i * 20;
        const up = c >= o;
        const color = up ? "#22a06b" : "#e5484d";
        const y = (v: number) => 112 - (v - 32) * 1.75;
        return (
          <g key={i}>
            <line x1={x} x2={x} y1={y(h)} y2={y(l)} stroke={color} strokeWidth="1.5" />
            <rect x={x - 5} width="10" y={y(Math.max(o, c))} height={Math.max(2, Math.abs(c - o) * 1.75)} fill={color} rx="1" />
          </g>
        );
      })}
    </svg>
  );
};

const Index = () => {
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setActiveTestimonial((p) => (p + 1) % testimonials.length), 6000);
    return () => clearInterval(timer);
  }, []);

  const t = testimonials[activeTestimonial];

  return (
    <main className="min-h-screen">
      <SEO
        title="Bullio Academy | Professional Trading Education"
        description="Learn to trade like a professional with Bullio Academy. A structured curriculum, live market analysis, and a community of 2,400+ active traders."
        keywords={["trading education", "trading course", "price action", "technical analysis", "risk management", "trading psychology"]}
        jsonLd={[organizationSchema(), websiteSchema(), faqSchema(homeFaqs)]}
      />
      {/* Hero */}
      <section className="relative overflow-hidden hero-light px-4 pb-10 pt-8 sm:px-6 sm:pb-14 sm:pt-16 lg:pt-20">
        <div className="relative z-10 mx-auto container-page">
          <div className="grid items-center gap-10 lg:grid-cols-[1.05fr_1fr] lg:gap-12">
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 rounded-full border border-secondary/25 bg-secondary/5 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-secondary sm:px-4 sm:text-[11px] sm:tracking-wider">
                <Sparkles size={12} />
                Trading education for a brighter tomorrow
              </div>

              <h1 className="mt-5 text-[2rem] font-bold leading-[1.08] tracking-tight text-primary sm:text-5xl lg:text-[3.4rem] 2xl:text-6xl" style={{ textWrap: "balance" }}>
                <span className="word-in block" style={{ animationDelay: "150ms" }}>
                  Learn trading from leading voices.
                </span>
                <span className="word-in block text-secondary" style={{ animationDelay: "450ms" }}>
                  Build your edge with the right support.
                </span>
              </h1>
              <p
                className="word-in mx-auto mt-5 max-w-2xl text-[15px] leading-relaxed text-muted-foreground sm:text-base lg:mx-0"
                style={{ animationDelay: "750ms", textWrap: "pretty" }}
              >
                Bullio Academy is a trading-education platform with 300+ hours of courses, lectures, and
                masterclasses from leading names in trading education, plus live trading rooms,
                virtual-money practice, and a personal education advisor.
              </p>
              <p
                className="word-in mx-auto mt-3 max-w-2xl text-[15px] leading-relaxed text-muted-foreground sm:text-base lg:mx-0"
                style={{ animationDelay: "850ms" }}
              >
                New to trading? Start with the basics. Already trading? Sharpen your process.
              </p>

              <div className="mt-7 mx-auto grid w-full max-w-max justify-items-center gap-5 sm:mt-9 lg:mx-0 lg:justify-items-start">
                <div className="grid w-full grid-cols-1 gap-4 sm:w-max sm:grid-cols-[auto_auto] sm:items-center">
                  <EnrollButton
                    size="lg"
                    wrapperClassName="w-full sm:w-auto"
                    className="group w-full h-14 px-7 text-base font-semibold bg-secondary hover:bg-secondary/90 btn-premium shadow-lg shadow-secondary/25 sm:w-auto"
                  >
                    Book your free call
                    <ArrowRight size={18} className="ml-2 transition-transform group-hover:translate-x-1" />
                  </EnrollButton>
                  <Link to="/curriculum" className="w-full sm:w-auto">
                    <Button variant="outline" size="lg" className="w-full h-14 px-6 text-base font-medium border-2 border-primary/15 bg-white/80 text-primary hover:bg-primary/[0.04] hover:border-primary/30 backdrop-blur sm:w-auto">
                      Explore the curriculum
                    </Button>
                  </Link>
                </div>

                {/* Social-proof row under buttons */}
                <div className="grid w-full justify-items-center gap-3 text-xs text-muted-foreground sm:grid-cols-[auto_auto_auto] sm:items-center sm:justify-center sm:gap-5">
                  <div className="flex items-center gap-2.5 whitespace-nowrap">
                    <div className="flex -space-x-2">
                      {["from-secondary to-accent", "from-primary to-secondary", "from-accent to-primary"].map((g, i) => (
                        <div key={i} className={`h-7 w-7 rounded-full border-2 border-white bg-gradient-to-br ${g} shadow-sm`} />
                      ))}
                    </div>
                    <span className="text-sm font-semibold text-foreground">2,400+ members</span>
                  </div>
                  <span className="hidden h-4 w-px bg-border/70 sm:block" />
                  <div className="flex items-center justify-center gap-1.5 whitespace-nowrap">
                    <div className="flex items-center gap-0.5">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} size={14} className="fill-amber-400 text-amber-400" />
                      ))}
                    </div>
                    <span className="text-sm font-semibold text-foreground">4.9</span>
                    <GoogleGlyph className="h-3.5 w-3.5" />
                    <span className="text-muted-foreground">380+ reviews</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Photo panel. The image is cropped to its own aspect ratio, so
                the floating card can be placed in percentages and lands on
                the same spot at every width. */}
            <div className="relative mx-auto w-full max-w-xl lg:max-w-none">
              <div className="relative aspect-[1160/900] overflow-hidden rounded-3xl shadow-2xl shadow-primary/10 ring-1 ring-border/60">
                <img
                  src={bullioHomeHero}
                  srcSet={`${bullioHomeHero640} 640w, ${bullioHomeHero800} 800w, ${bullioHomeHero} 1160w`}
                  sizes="(min-width: 640px) 576px, calc(100vw - 32px)"
                  alt="A student learning at his laptop while Bullio, the academy mascot, points at the screen"
                  width={1160}
                  height={900}
                  fetchPriority="high"
                  decoding="async"
                  className="h-full w-full object-cover"
                />
                <div className="absolute left-[3%] top-[5%] rounded-2xl border border-border/60 bg-white/95 p-2 shadow-lg backdrop-blur sm:p-4">
                  <ul className="space-y-0.5 whitespace-nowrap text-[9px] font-medium leading-snug text-primary sm:space-y-1 sm:text-xs">
                    {["Knowledge", "Practice", "Support", "Real Progress"].map((t) => (
                      <li key={t} className="flex items-center gap-1.5">
                        <Check size={11} strokeWidth={3} className="shrink-0 text-secondary" />
                        {t}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Five highlights */}
          <div className="mt-10 grid grid-cols-2 gap-y-6 rounded-2xl border border-border/60 bg-white/90 px-4 py-6 shadow-sm backdrop-blur sm:mt-14 sm:grid-cols-3 lg:grid-cols-5 lg:divide-x lg:divide-border/60">
            {highlights.map((h, i) => (
              <div key={h.title} className={`flex flex-col items-center px-3 text-center ${i === 4 ? "col-span-2 sm:col-span-1" : ""}`}>
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-secondary/10 text-secondary">
                  <h.icon size={20} />
                </div>
                <div className="mt-3 text-sm font-semibold text-primary">{h.title}</div>
                <div className="mt-1 text-xs leading-snug text-muted-foreground">{h.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Bullio */}
      <section className="px-4 py-14 sm:px-6 sm:py-20">
        <div className="mx-auto grid container-page items-center gap-10 lg:grid-cols-[1fr_1.15fr] lg:gap-14">
          <Reveal>
            <Eyebrow>Why Bullio Academy</Eyebrow>
            <h2 className="mt-4 text-3xl font-bold leading-tight text-primary sm:text-4xl">
              One platform. A clearer path.
              <br />
              A stronger you.
            </h2>
            <p className="mt-5 text-[15px] leading-relaxed text-muted-foreground">
              Bullio Academy brings together everything you need to learn trading in one place, not
              scattered across random courses. Follow a structured path, get personal guidance, and
              learn from experienced traders in real market conditions.
            </p>
            <p className="mt-4 text-[15px] leading-relaxed text-muted-foreground">
              It's more than videos. It's education, practice, support, and a community that helps you
              stay on track.
            </p>
            <div className="mt-7">
              <BookCallButton />
            </div>
          </Reveal>
          <div className="grid gap-4 sm:grid-cols-2">
            {whyCards.map((c, i) => (
              <Reveal key={c.title} delay={i * 70} className="h-full">
                <div className="h-full rounded-2xl border border-border/70 bg-white p-6 shadow-sm transition-all hover-lift hover:border-secondary/30">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-secondary/10 text-secondary">
                    <c.icon size={20} />
                  </div>
                  <h3 className="mt-4 font-semibold text-primary">{c.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{c.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Flagship topics */}
      <section className="bg-muted/40 px-4 py-14 sm:px-6 sm:py-20">
        <div className="mx-auto container-page">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <Eyebrow>Our course library</Eyebrow>
              <h2 className="mt-4 text-3xl font-bold text-primary sm:text-4xl">Explore the flagship topics</h2>
            </div>
            <p className="max-w-md text-[15px] leading-relaxed text-muted-foreground">
              In-depth courses on the strategies, tools, and mindset that matter, for new and
              experienced traders alike.
            </p>
          </div>

          <div className="mt-8 grid grid-cols-1 gap-4 min-[480px]:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
            {flagshipTopics.map((f, i) => (
              <Reveal key={f.title} delay={i * 60} className="h-full">
                <Link
                  to="/curriculum"
                  className="group flex h-full flex-col rounded-2xl border border-border/70 bg-white p-5 shadow-sm transition-all hover-lift hover:border-secondary/30"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-secondary/10 text-secondary">
                    <f.icon size={24} />
                  </div>
                  <h3 className="mt-4 font-semibold text-primary">{f.title}</h3>
                  <p className="mt-1.5 flex-1 text-sm leading-relaxed text-muted-foreground">{f.desc}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-secondary">
                    {f.courses} courses
                    <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>

          <Link
            to="/curriculum"
            className="group mt-4 flex items-center gap-4 rounded-2xl border border-border/70 bg-white p-4 shadow-sm transition-colors hover:border-secondary/30 sm:p-5"
          >
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-secondary/10 text-secondary">
              <LayoutGrid size={20} />
            </div>
            <span className="shrink-0 font-semibold text-primary">+ 15 more topics</span>
            <span className="hidden flex-1 text-sm leading-relaxed text-muted-foreground md:block">
              Including Trading Fundamentals, Swing Trading, Scalping, Trading Psychology, Candlestick
              Reading, Price Action, Technical Indicators, Order Flow, Stocks, Forex, Crypto, Fundamental
              Analysis, Economic News, Building a Trading Plan, Backtesting and Algorithmic Trading.
            </span>
            <span className="ml-auto flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-border/70 text-primary transition-transform group-hover:translate-x-0.5">
              <ChevronRight size={16} />
            </span>
          </Link>
        </div>
      </section>

      {/* Personal advisor */}
      <section className="px-4 py-14 sm:px-6 sm:py-20">
        <div className="mx-auto grid container-page items-center gap-8 lg:grid-cols-[1fr_1.1fr_0.85fr] lg:gap-10">
          <div className="relative aspect-[4/3] overflow-hidden rounded-3xl shadow-xl shadow-primary/10 ring-1 ring-border/60">
            <img
              src={advisorPhoto}
              srcSet={`${advisorPhoto600} 600w, ${advisorPhoto} 1122w`}
              sizes="(min-width: 1024px) 385px, (min-width: 640px) 720px, calc(100vw - 32px)"
              alt="A Bullio education advisor smiling on a call with a student"
              width={1122}
              height={1402}
              loading="lazy"
              className="h-full w-full object-cover object-[50%_30%]"
            />
            <p className="font-hand absolute left-5 top-5 -rotate-6 text-2xl leading-tight text-primary [text-shadow:0_0_6px_rgba(255,255,255,0.95)]">
              You're
              <br />
              not alone
              <br />
              in this.
              <span className="mt-1 block h-0.5 w-12 rounded bg-secondary/70" />
            </p>
          </div>

          <div>
            <Eyebrow>Our personal advisor</Eyebrow>
            <h2 className="mt-4 text-3xl font-bold leading-tight text-primary sm:text-4xl">
              Not just more videos.
              <br />
              Someone to help it click.
            </h2>
            <p className="mt-5 text-[15px] leading-relaxed text-muted-foreground">
              Every Bullio student gets a personal education advisor who helps you choose what to learn
              next, get set up with the right tools, and answers your questions as you progress.
            </p>
            <ul className="mt-5 space-y-2.5">
              {advisorPoints.map((p) => (
                <li key={p} className="flex items-start gap-2.5 text-[15px] text-foreground/85">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-secondary text-white">
                    <Check size={12} strokeWidth={3} />
                  </span>
                  {p}
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-2xl border border-border/70 bg-white p-6 shadow-sm">
            <Eyebrow>How it works</Eyebrow>
            <ol className="mt-5 space-y-5">
              {advisorSteps.map((s, i) => (
                <li key={s.title} className="flex gap-3.5">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-secondary/10 text-sm font-bold text-secondary">
                    {i + 1}
                  </span>
                  <div>
                    <div className="text-sm font-semibold text-primary">{s.title}</div>
                    <div className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{s.desc}</div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* Student stories */}
      <section className="bg-muted/40 px-4 py-14 sm:px-6 sm:py-20">
        <div className="mx-auto container-page">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <Eyebrow>Student stories</Eyebrow>
              <h2 className="mt-4 text-3xl font-bold text-primary sm:text-4xl">Real student stories</h2>
            </div>
            <p className="max-w-md text-[15px] leading-relaxed text-muted-foreground">
              Thousands of traders have already joined Bullio Academy. Here's what a few of them have to
              say.
            </p>
          </div>

          {/* Student videos, added from Admin > Testimonials. Renders nothing
              until at least one is published. */}
          <VideoTestimonials limit={3} className="mt-8" />

          <div className="mt-6 flex flex-col items-center gap-5 rounded-2xl border border-border/70 bg-white p-6 shadow-sm md:flex-row md:gap-8 md:px-8">
            <Quote size={40} strokeWidth={1.5} className="hidden shrink-0 text-secondary/25 md:block" />
            <div className="flex-1 text-center">
              <div className="flex justify-center gap-0.5">
                {[...Array(5)].map((_, s) => (
                  <Star key={s} size={15} className="fill-amber-400 text-amber-400" />
                ))}
              </div>
              <p key={t.name} className="mt-3 text-[15px] leading-relaxed text-foreground animate-in fade-in duration-500" style={{ textWrap: "pretty" }}>
                "{t.text}"
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-gradient-to-br from-secondary to-accent text-sm font-bold text-white">
                {t.name.split(" ").map((n) => n[0]).join("")}
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">{t.name}</p>
                <p className="text-xs text-muted-foreground">{t.role}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setActiveTestimonial((p) => (p - 1 + testimonials.length) % testimonials.length)}
                aria-label="Previous testimonial"
                className="flex h-9 w-9 items-center justify-center rounded-full border border-border/70 bg-white text-primary transition-colors hover:border-secondary/40 hover:bg-secondary/5"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                onClick={() => setActiveTestimonial((p) => (p + 1) % testimonials.length)}
                aria-label="Next testimonial"
                className="flex h-9 w-9 items-center justify-center rounded-full border border-border/70 bg-white text-primary transition-colors hover:border-secondary/40 hover:bg-secondary/5"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Learn in real time */}
      <section className="px-4 py-14 sm:px-6 sm:py-20">
        <div className="mx-auto container-page">
          <Eyebrow>Learn. Practice. Improve.</Eyebrow>
          <h2 className="mt-4 text-3xl font-bold text-primary sm:text-4xl">
            Learn in real time. Then practice it yourself.
          </h2>

          <div className="mt-8 grid gap-5 lg:grid-cols-2">
            {/* Live trading rooms. The dark panel and chart stand in for the
                photo until it arrives. */}
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#0f1d2e] via-[#14283d] to-[#0f1d2e] p-7 text-white shadow-lg sm:p-9">
              <div className="absolute inset-y-0 right-0 w-3/5 opacity-60 [mask-image:linear-gradient(to_left,black,transparent)]">
                <div className="absolute inset-x-4 top-10 h-40">
                  <MiniChart dark />
                </div>
              </div>
              <span className="absolute right-5 top-5 inline-flex items-center gap-1.5 rounded-md bg-red-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">
                <span className="h-1.5 w-1.5 rounded-full bg-white" /> Live
              </span>
              <div className="relative max-w-sm">
                <h3 className="text-2xl font-bold text-white">Live trading rooms</h3>
                <p className="mt-3 text-[15px] leading-relaxed text-white/75">
                  Watch experienced traders analyze the markets in real time, ask questions, and learn
                  their process.
                </p>
                <div className="mt-6">
                  <EnrollButton
                    size="lg"
                    wrapperClassName="w-full sm:w-auto"
                    className="group h-12 w-full bg-secondary px-6 text-base font-semibold hover:bg-secondary/90 sm:w-auto"
                  >
                    Join a live room
                    <ArrowRight size={18} className="ml-2 transition-transform group-hover:translate-x-1" />
                  </EnrollButton>
                </div>
              </div>
            </div>

            {/* Demo trading account */}
            <div className="relative overflow-hidden rounded-3xl border border-border/70 bg-gradient-to-br from-emerald-50/70 to-white p-7 shadow-sm sm:p-9">
              <div className="grid gap-6 sm:grid-cols-[1fr_1fr] sm:items-center">
                <div>
                  <h3 className="text-2xl font-bold text-primary">Demo trading account</h3>
                  <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">
                    Practice what you learn with virtual money in a realistic trading environment.
                  </p>
                  <div className="mt-6">
                    <EnrollButton
                      size="lg"
                      wrapperClassName="w-full sm:w-auto"
                      className="group h-12 w-full bg-secondary px-6 text-base font-semibold hover:bg-secondary/90 sm:w-auto"
                    >
                      Start practicing
                      <ArrowRight size={18} className="ml-2 transition-transform group-hover:translate-x-1" />
                    </EnrollButton>
                  </div>
                </div>
                <div className="relative">
                  <span className="absolute -top-3 right-0 z-10 rounded-lg bg-secondary px-3 py-1.5 text-xs font-semibold text-white shadow-md">
                    Practice. Learn. Grow.
                  </span>
                  <div className="h-36 rounded-xl border border-border/60 bg-white p-3 pt-5 shadow-md">
                    <MiniChart />
                  </div>
                  <div className="absolute -bottom-4 left-3 rounded-xl border border-border/60 bg-white px-4 py-2.5 shadow-lg">
                    <div className="text-lg font-bold text-primary">$100,000</div>
                    <div className="text-[11px] text-muted-foreground">Virtual Balance</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 pb-16 sm:px-6 sm:pb-24">
        <div className="relative mx-auto container-page overflow-hidden rounded-3xl bg-primary px-6 pt-10 sm:px-10 md:pt-0">
          <div className="grid items-end gap-6 md:grid-cols-[1.3fr_1fr]">
            <div className="md:py-14">
              <Eyebrow light>Your next step</Eyebrow>
              <h2 className="mt-4 text-3xl font-bold text-white sm:text-4xl">Ready to build your trading edge?</h2>
              <p className="mt-3 max-w-lg text-[15px] leading-relaxed text-white/75">
                Book a free call with our team to learn more about Bullio Academy and find out if it's
                the right fit for your goals.
              </p>
              <div className="mt-7">
                <BookCallButton />
              </div>
            </div>
            <div className="relative mx-auto h-56 w-full max-w-xs sm:h-64">
              <div className="absolute bottom-0 left-1/2 h-48 w-48 -translate-x-1/2 rounded-full bg-secondary/20 blur-2xl" />
              <img
                src={bullioWave}
                srcSet={`${bullioWave360} 360w, ${bullioWave} 640w`}
                sizes="170px"
                alt="Bullio waving"
                width={640}
                height={1163}
                loading="lazy"
                className="absolute bottom-0 left-[10%] h-[118%] w-auto object-contain object-top drop-shadow-2xl"
              />
              <p className="font-hand absolute right-0 top-4 -rotate-6 text-xl leading-tight text-white sm:text-2xl">
                Same market.
                <br />
                A brighter you.
                <span className="mt-1 block h-0.5 w-12 rounded bg-white/60" />
              </p>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Index;
